/* @flow */

import { runInAction } from 'mobx';
import { Dispatcher } from 'flux';

// eslint-disable-next-line import/no-cycle
import StateStore from './store';

// object which represent a null value
const NULL_OBJECT = {};

/**
 * This wrapper will flatten 'undefined action' error
 * when trying to access a unknown store
 */
const proxify = (target: Object) =>
  new Proxy(target, {
    get(obj, prop) {
      if (prop in obj) return obj[prop];

      if (obj !== NULL_OBJECT) {
        console.warn(`[eko-flobx] ${prop} doesn't exist in ${obj._name} store`);
      }

      return () => {};
    },
  });

/**
 * The dispatcher is used to emit flux actions to all stores
 *
 * We are going to subclass the default flux dispatcher to make it store return values
 * from dispatches, so they can be passed to other stores via waitFor;
 */
class EkoDispatcher extends Dispatcher {
  _storeNames = new Set();

  register(store: StateStore, secured: boolean = true) {
    this._addStore(store, secured);
    return super.register(payload => store.reduce(payload));
  }

  /*
    Wraps the flux native dispatch with runInAction for a performance reason to
    avoid mobx recomputing during the dispatch but does it only in the end.
  */
  dispatch(payload: any) {
    runInAction(() => super.dispatch(payload));
  }

  _addStore(store: StateStore, secured: boolean) {
    // make sure the store name isn't already registered
    if (this._storeNames.has(store._name)) {
      throw new Error(`[eko-flobx] A store named ${store._name} already exists`);
    }

    if (!(store._name in this)) {
      // optional registration of the store's name to prevent overriding
      if (secured) this._storeNames.add(store._name);
      this[store._name] = proxify({
        _name: store._name,
      });
    }

    store._reducers.forEach((_, action) => {
      this._addReducer(store._name, action);
    });

    store._effects.forEach((fn, action) => {
      this._addEffect(store._name, action, fn);
    });
  }

  _addReducer(name: string, action: string) {
    // action name does not concern this store
    if (!action.startsWith(`${name}.`)) return;

    // strip out store name
    const key = action.replace(`${name}.`, '');

    this[name][key] = (...args) => this._dispatch(`${action}`, args);
  }

  _addEffect(name: string, action: string, fn: function) {
    this[name][action] = fn;
  }

  _dispatch(type: string, payload: any[]) {
    this.dispatch({ type, payload });
  }
}

/**
 * the EkoDispatcher is wrapped in a proxy to
 * handle silently 'unknown store' errors
 */
export default new Proxy(new EkoDispatcher(), {
  get(obj, prop) {
    if (prop in obj) return obj[prop];

    if (obj !== NULL_OBJECT) {
      console.warn(`[eko-flobx] ${prop} store isn't loaded.`);
    }

    return proxify(NULL_OBJECT);
  },
});
